// Layer 3 (Transformation) — pure derivations for the Strategy tab (v19 M9).

import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import { diffMinutes, formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { RISK_ENTRIES } from '@/versions/v1/modules/risk/seed';
import type { PmTask } from './pm.seed';
import { workOrderRows, type WorkOrderRow } from './Scene.transform';
import {
  complianceTone,
  pmKpis,
  pmStatus,
  type PmKpis,
} from './PmSchedule.transform';
import { ITEM_SUBPROCESS } from './downtime-events.seed';
import type { DowntimeItemId } from './downtime.seed';
import type { ConditionMonitoringPoint } from './Scene.transform';
import {
  AREA_LABEL,
  CM_POINT_ITEM,
  CONDITION_LABEL,
  FMEA_TASK_TYPE_COUNTS,
  MAINTAINABLE_ITEMS,
  SHUTDOWN_WINDOWS,
  WORK_ORDER_ITEM_BY_ASSET_LABEL,
  type CriticalityTier,
  type MaintainableItem,
  type ShutdownWindow,
} from './strategy.seed';

export function maintainableItems(): readonly MaintainableItem[] {
  return MAINTAINABLE_ITEMS;
}

export function shutdownWindows(): readonly ShutdownWindow[] {
  return SHUTDOWN_WINDOWS;
}

export function shutdownWindowById(id: string | undefined): ShutdownWindow | undefined {
  if (!id) return undefined;
  return SHUTDOWN_WINDOWS.find((row) => row.id === id);
}

/** "10 Jun · 12h" — the items table's next-shutdown cell; a dash when no
 *  window is linked rather than inventing one (v19 M9 §6 — no calendar exists). */
export function nextShutdownLabel(item: MaintainableItem): string {
  const window = shutdownWindowById(item.shutdownWindowId);
  if (!window) return '—';
  return `${window.plannedStart} · ${window.durationHours}h`;
}

const TIER_RANK: Record<CriticalityTier, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };

/** Items table sorted criticality-first — the reader's attention should land
 *  on the highest-risk item, per the same theory-of-constraints argument
 *  Module 6 applies to risk (v19 M9 §8). */
export function itemsByCriticality(): readonly MaintainableItem[] {
  return [...MAINTAINABLE_ITEMS].sort((a, b) => TIER_RANK[a.criticality] - TIER_RANK[b.criticality]);
}

export type TaskTypeSlice = {
  taskType: string;
  occurrences: number;
  pct: number;
};

/** v19 M9 §3's real full-FMEA task-type counts (not scoped to the ten items
 *  on this page) — sorted highest-count first so the bar list reads as a
 *  ranked chart. `pct` scales to the largest count, not the counts' sum, so
 *  the leading bar fills the track instead of every bar reading near-empty. */
export function taskTypeDistribution(): readonly TaskTypeSlice[] {
  const max = FMEA_TASK_TYPE_COUNTS.reduce((peak, row) => Math.max(peak, row.occurrences), 0);
  return [...FMEA_TASK_TYPE_COUNTS]
    .sort((a, b) => b.occurrences - a.occurrences)
    .map((row) => ({
      taskType: row.taskType,
      occurrences: row.occurrences,
      pct: max > 0 ? (row.occurrences / max) * 100 : 0,
    }));
}

export function criticalityPillTone(tier: CriticalityTier): 'danger' | 'warning' | 'default' {
  if (tier === 'Critical') return 'danger';
  if (tier === 'High' || tier === 'Medium') return 'warning';
  return 'default';
}

// ---------------------------------------------------------------------------
// The asset register (a20-maintenance S6a) — one row per maintainable item,
// joining four sources that already exist: the item seed, the PM schedule, the
// condition-monitoring inventory and the risk register. Nothing here types a
// fact; every field is a read of one of those four.
//
// Three joins are worth naming, because each is a place a second vocabulary
// could creep in:
//
//   (a) Area. The downtime event log's `ITEM_SUBPROCESS` is the primary link,
//       and `MaintainableItem.subprocessId` covers only the two items the log
//       does not carry. The long `SUBPROCESSES.name` never reaches a cell —
//       `AREA_LABEL` decodes the id to the risk register's own one-word
//       `circuit` string.
//   (b) Condition. The inventory names assemblies and maps to item ids through
//       `CM_POINT_ITEM`; an item with no point has no condition, and the cell
//       says so with the muted dash rather than a guessed chip.
//   (c) Criticality. The pill is the tier; the RPN beside it is read from the
//       register through `riskId`, and an item with no register row shows the
//       dash. The register's own `label` is what the overdue card names, not
//       the PM instance's `asset` — the two coincide today because the plan was
//       typed from the register, and keeping the reads distinct is what makes
//       the coincidence checkable.
//
// durable: the asset register's item/area/criticality/PM joins (ADR-0012).

export type AssetRegisterRow = {
  id: string;
  label: string;
  /** The item's own deep-read string; it rides the item cell's `title`. */
  conditionSignal: string;
  areaId: string;
  areaLabel: string;
  criticality: CriticalityTier;
  riskId: string | null;
  rpn: number | null;
  /** RK-02 reads its current RPN from `fm-trunnion`; null when no risk row maps. */
  riskLabel: string | null;
  taskType: string;
  frequency: string;
  /** The latest completed instance's completion instant, or null. */
  lastPm: SeriesTime | null;
  /** `06 Jun`, or the muted dash when the item has no completed instance. */
  lastPmLabel: string;
  /** The earliest open instance's due instant, or null. */
  nextPm: SeriesTime | null;
  /** `10 Jun`, or the muted dash when the item has no open instance. */
  nextPmLabel: string;
  overdue: boolean;
  /** Whole days past due, the same read the overdue KPI card prints. */
  daysOverdue: number;
  condition: string | null;
  conditionLabel: string;
  conditionTone: 'danger' | 'warning' | 'default';
  nextShutdownLabel: string;
  openWorkOrderCount: number;
};

const OPEN_WORK_ORDER_STATUSES = new Set(['OPEN', 'IN PRG']);

/** Open WOs joined only by the explicit asset-label map; scheduled rows are
 *  planned work, not open work orders. */
export function openWorkOrdersForItem(itemId: string): WorkOrderRow[] {
  return workOrderRows().filter(
    (row) =>
      WORK_ORDER_ITEM_BY_ASSET_LABEL[row.asset] === itemId &&
      OPEN_WORK_ORDER_STATUSES.has(row.status),
  );
}

/** The item's subprocess: the event log's link where it has one, the seed's own
 *  field for the two items the log does not carry. */
export function itemSubprocessId(item: MaintainableItem): string | undefined {
  return ITEM_SUBPROCESS[item.id as DowntimeItemId] ?? item.subprocessId;
}

const CONDITION_RANK: Record<string, number> = { warn: 0, rising: 1, stable: 2 };

/** The worst chip across an item's points: `warn` beats `rising` beats
 *  `stable`, and an unrecognised chip sits behind all three. */
function worstCondition(chips: readonly string[]): string | null {
  if (chips.length === 0) return null;
  return [...chips].sort(
    (a, b) => (CONDITION_RANK[a] ?? 99) - (CONDITION_RANK[b] ?? 99),
  )[0] as string;
}

/** The item's worst monitoring point — the point its Condition chip reports,
 *  and the one the register's Condition cell selects on Condition monitoring
 *  (polish.P21, comp C's BM01 row). Null when the item has no point. */
export function worstPointForItem(
  itemId: string,
  points: readonly ConditionMonitoringPoint[],
): string | null {
  const mine = points.filter((point) => CM_POINT_ITEM[point.id] === itemId && point.statusChip !== undefined);
  const worst = [...mine].sort(
    (a, b) => (CONDITION_RANK[a.statusChip!] ?? 99) - (CONDITION_RANK[b.statusChip!] ?? 99),
  )[0];
  return worst?.id ?? null;
}

/** The item the register highlights: the one carrying the route's leading
 *  point — the first point that opened a case, the same default the panel row
 *  draws. Null when no point has a case. */
export function leadingItemId(points: readonly ConditionMonitoringPoint[]): string | null {
  const lead = points.find((point) => point.caseId);
  return lead ? CM_POINT_ITEM[lead.id] ?? null : null;
}

export function conditionTone(chip: string | null): 'danger' | 'warning' | 'default' {
  if (chip === 'warn') return 'danger';
  if (chip === 'rising') return 'warning';
  return 'default';
}

/** The register's rows, criticality-sorted, each one a join across the item
 *  seed, the PM schedule, the monitoring points and the risk register. */
export function assetRegisterRows(
  items: readonly MaintainableItem[],
  tasks: readonly PmTask[],
  points: readonly ConditionMonitoringPoint[],
  cursor: SeriesTime,
): AssetRegisterRow[] {
  const cursorFloor = `${cursor.slice(0, 10)}T00:00`;

  return items.map((item) => {
    const mine = tasks.filter((task) => task.itemId === item.id);
    const done = mine
      .filter((task) => task.completedAt !== undefined)
      .sort((a, b) => ((a.completedAt ?? '') < (b.completedAt ?? '') ? 1 : -1));
    const open = mine
      .filter((task) => task.completedAt === undefined)
      .sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0));

    const lastPm = (done[0]?.completedAt ?? null) as SeriesTime | null;
    const nextRow = open[0];
    const nextPm = (nextRow?.dueDate ?? null) as SeriesTime | null;
    // Status comes from `pmStatus`, never from a second date comparison; the
    // day count is the expression `pmKpis` makes for its own `worst`.
    const overdue = nextRow !== undefined && pmStatus(nextRow, cursor) === 'overdue';
    const daysOverdue =
      overdue && nextRow !== undefined
        ? diffMinutes(cursorFloor, `${nextRow.dueDate.slice(0, 10)}T00:00`) / (24 * 60)
        : 0;

    const areaId = itemSubprocessId(item) ?? '';
    const risk = RISK_ENTRIES.find((row) => row.id === item.riskId);
    const chips = points
      .filter((point) => CM_POINT_ITEM[point.id] === item.id)
      .map((point) => point.statusChip)
      .filter((chip): chip is string => chip !== undefined);
    const condition = worstCondition(chips);

    return {
      id: item.id,
      label: item.label,
      conditionSignal: item.conditionSignal,
      areaId,
      areaLabel: AREA_LABEL[areaId] ?? areaId,
      criticality: item.criticality,
      riskId: item.riskId ?? null,
      rpn: risk?.rpn ?? null,
      riskLabel: risk === undefined ? null : `${risk.id} · RPN ${risk.rpn}`,
      taskType: item.taskType,
      frequency: item.frequency,
      lastPm,
      lastPmLabel: lastPm === null ? '—' : formatDayMonth(lastPm),
      nextPm,
      nextPmLabel: nextPm === null ? '—' : formatDayMonth(nextPm),
      overdue,
      daysOverdue,
      condition,
      conditionLabel: condition === null ? '—' : (CONDITION_LABEL[condition] ?? condition),
      conditionTone: conditionTone(condition),
      nextShutdownLabel: nextShutdownLabel(item),
      openWorkOrderCount: openWorkOrdersForItem(item.id).length,
    };
  });
}

// ---------------------------------------------------------------------------
// The KPI strip above the register. Four cards, one per read the register
// already carries; the strip exists so the reader sees the shape of the
// maintenance position before the ten rows that make it up.
//
// Two rulings are load-bearing:
//
//   (a) `Condition alerts` counts `warn` only. A `rising` point is a trend the
//       column names `Rising`; counting it would make the card disagree with
//       the rows beneath it by definition. The card counts alerts; the column
//       shows both.
//   (b) `PM compliance` is `pmKpis().compliance` unchanged — the same object
//       the PM Schedule tab's card prints, so the two tabs cannot publish two
//       different compliances for one schedule.
//
// The overdue card always measures: with nothing overdue its sub is the count
// of items carrying an open PM, never a sentence about there being none.
//
// durable: the Strategy tab's KPI strip over the register (ADR-0012).

export type StrategyKpis = {
  items: { count: number; onRegister: number; value: string; sub: string };
  overdue: {
    count: number;
    worst: { label: string; daysOverdue: number } | null;
    value: string;
    sub: string;
    tone: VizTone;
  };
  alerts: {
    count: number;
    points: number;
    value: string;
    sub: string;
    tone: VizTone;
  };
  compliance: PmKpis['compliance'] & { tone: VizTone };
};

export function strategyKpis(
  rows: readonly AssetRegisterRow[],
  tasks: readonly PmTask[],
  points: readonly ConditionMonitoringPoint[],
  cursor: SeriesTime,
): StrategyKpis {
  const onRegister = rows.filter((row) => row.riskId !== null).length;
  // The worst overdue row's own register label, with the same day count
  // `pmKpis` computes for its `worst`.
  const overdueRows = rows.filter((row) => row.overdue);
  let worst: { label: string; daysOverdue: number } | null = null;
  for (const row of overdueRows) {
    if (worst === null || row.daysOverdue > worst.daysOverdue) {
      worst = { label: row.label, daysOverdue: row.daysOverdue };
    }
  }
  const openCount = rows.filter((row) => row.nextPm !== null).length;
  const alerts = points.filter((point) => point.statusChip === 'warn').length;
  const compliance = pmKpis(tasks, cursor).compliance;

  return {
    items: {
      count: rows.length,
      onRegister,
      value: String(rows.length),
      sub: `${onRegister} on the risk register`,
    },
    overdue: {
      count: overdueRows.length,
      worst,
      value: String(overdueRows.length),
      sub: worst === null ? `${openCount} with an open PM` : `${worst.label} · ${worst.daysOverdue} days`,
      tone: overdueRows.length > 0 ? 'bad' : 'ok',
    },
    alerts: {
      count: alerts,
      points: points.length,
      value: String(alerts),
      sub: `of ${points.length} points`,
      tone: alerts > 0 ? 'warn' : 'ok',
    },
    compliance: { ...compliance, tone: complianceTone(compliance.pct) },
  };
}
